import { useRef, useEffect, useState, useMemo } from 'react';

type Options = {
  enabled?: boolean;
};

const defaultOptions: Options = {
  enabled: true,
};

/** Hook for scrolling a div using the mouse */
export const useMouseScroll = (options?: Options) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });

  const optionsWithDefaults = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options]
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !optionsWithDefaults.enabled) return;

    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      setStartPos({ x: e.clientX, y: e.clientY });
      setScrollStart({ x: element.scrollLeft, y: element.scrollTop });
      element.style.cursor = 'grabbing';
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startPos.x;
      const deltaY = e.clientY - startPos.y;

      element.scrollLeft = scrollStart.x - deltaX;
      element.scrollTop = scrollStart.y - deltaY;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      element.style.cursor = 'grab';
    };

    element.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    element.style.cursor = 'grab';

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startPos, scrollStart, optionsWithDefaults]);

  return { ref };
};
